import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  ToggleButton, 
  ToggleButtonGroup, 
  Toolbar, 
  Tooltip,
  IconButton 
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { 
  getSegmentDescription, 
  getElementDescription
} from '../utils/eancomSegmentDescriptions';

/**
 * Component to display raw EDI content with formatting options
 */
const RawEdiViewer = ({ content, validationResults, parsedData }) => {
  const [formatType, setFormatType] = useState('segment');
  const [copySuccess, setCopySuccess] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  
  // Create a mapping of segment indices to validation errors
  const createErrorMap = () => {
    const errorMap = new Map();
    
    if (!validationResults || !parsedData?.segments) return errorMap;
    
    // Process errors separately to maintain their classification
    const processIssues = (issues, issueType) => {
      if (!issues) return;
      
      issues.forEach(issue => {
        if (issue.location && issue.location.includes('segment:')) {
          // Extract segment info from location
          const segmentMatch = issue.location.match(/segment:(\w+)(?:\[(\d+)\])?/);
          if (segmentMatch) {
            const segmentType = segmentMatch[1];
            const segmentIndex = segmentMatch[2] ? parseInt(segmentMatch[2]) : null;
            
            // Find segment by type and index
            let targetSegmentIndex = -1;
            if (segmentIndex !== null) {
              // Use specific index
              targetSegmentIndex = segmentIndex;
            } else {
              // Find first segment of this type
              targetSegmentIndex = parsedData.segments.findIndex(seg => seg.id === segmentType);
            }
            
            if (targetSegmentIndex >= 0) {
              if (!errorMap.has(targetSegmentIndex)) {
                errorMap.set(targetSegmentIndex, []);
              }
              // Add the issue with explicit classification
              errorMap.get(targetSegmentIndex).push({
                ...issue,
                issueType: issueType  // Explicitly mark as 'error' or 'warning'
              });
            }
          }
        }
      });
    };
    
    // Process errors and warnings separately
    processIssues(validationResults.errors, 'error');
    processIssues(validationResults.warnings, 'warning');
    
    return errorMap;
  };
  
  const errorMap = createErrorMap();
  
  
  // Format raw content based on selected format type
  const formatContent = () => {
    if (!content) return '';
    
    switch (formatType) {
      case 'segment':
        // Format with segment separators and add syntax highlighting
        return highlightSegments(content.split("'").join("'\n"));
      case 'element':
        // Format with segment and element separators
        let formattedContent = content.split("'").join("'\n");
        return highlightElements(formattedContent.replace(/\+/g, '\n  +'));
      case 'raw':
      default:
        // No formatting, return as is
        return content;
    }
  };
  
  // Parse segment data to extract segment identifier and qualifiers
  const parseSegmentData = (line) => {
    if (!line) return { segmentId: '', qualifiers: [], composites: [] };
    
    // Extract segment identifier (first characters before the + sign)
    const segmentId = line.substring(0, line.indexOf('+')) || line.substring(0, 3);
    
    // Extract qualifiers (values immediately after + signs)
    const qualifiers = [];
    const composites = [];
    const parts = line.split('+');
    
    for (let i = 1; i < parts.length; i++) {
      // Extract the qualifier, which could be before a : if it's a composite element
      const part = parts[i];
      
      if (part.includes(':')) {
        // This is a composite element
        const qualifier = part.substring(0, part.indexOf(':'));
        qualifiers.push(qualifier);
        
        // Store the composite structure for detailed display
        const compositeElements = part.split(':');
        composites.push(compositeElements);
      } else {
        qualifiers.push(part);
        composites.push([part]);
      }
    }
    
    return { segmentId, qualifiers, composites };
  };
  
  // Get detailed description for a qualifier
  const getQualifierDescription = (segmentId, qualifier, position) => {
    if (!qualifier) return '';
    
    // Get element information for the position
    const elementInfo = getElementDescription(segmentId, position);
    
    if (!elementInfo) return '';
    
    // Try to get the qualifier explanation
    let qualifierDescription = '';
    
    if (elementInfo.values && elementInfo.values[qualifier]) {
      qualifierDescription = elementInfo.values[qualifier];
    } else if (elementInfo.explanations && elementInfo.explanations[qualifier]) {
      qualifierDescription = elementInfo.explanations[qualifier];
    }
    
    // Special handling for specific segments that have well-known qualifiers
    if (!qualifierDescription) {
      if (segmentId === 'DTM' && position === 1) {
        // Handle DTM qualifiers (common ones seen in the screenshot)
        if (qualifier === '137') {
          qualifierDescription = 'Document/message date/time';
        } else if (qualifier === '11') {
          qualifierDescription = 'Despatch date and or time';
        } else if (qualifier === '2') {
          qualifierDescription = 'Delivery date/time, requested';
        } else if (qualifier === '3') {
          qualifierDescription = 'Invoice date/time';
        } else if (qualifier === '17') {
          qualifierDescription = 'Delivery date/time, estimated';
        } else if (qualifier === '35') {
          qualifierDescription = 'Delivery date/time, actual';
        } else if (qualifier === '4') {
          qualifierDescription = 'Order date/time';
        } else if (qualifier === '10') {
          qualifierDescription = 'Shipment date/time, requested';
        } else if (qualifier === '50') {
          qualifierDescription = 'Goods receipt date/time';
        } else if (qualifier === '132') {
          qualifierDescription = 'Arrival date/time, estimated';
        } else if (qualifier === '131') {
          qualifierDescription = 'Shipment date/time, actual';
        } else if (qualifier === '171') {
          qualifierDescription = 'Reference date/time';
        } else if (qualifier === '191') {
          qualifierDescription = 'Expiry date of offer';
        } else if (qualifier === '36') {
          qualifierDescription = 'Expiry date';
        } else if (qualifier === '63') {
          qualifierDescription = 'Delivery date/time, latest';
        } else if (qualifier === '64') {
          qualifierDescription = 'Delivery date/time, earliest';
        } else if (qualifier === '21') {
          qualifierDescription = 'Payment period, due date';
        } else if (qualifier === '13') {
          qualifierDescription = 'Terms net due date';
        } else if (qualifier === '165') {
          qualifierDescription = 'Payment terms due date';
        } else if (qualifier === '140') {
          qualifierDescription = 'Payment due date';
        } else if (qualifier === '90') {
          qualifierDescription = 'Report start date';
        } else if (qualifier === '91') {
          qualifierDescription = 'Report end date';
        } else if (qualifier === '7') {
          qualifierDescription = 'Effective date/time';
        } else if (qualifier === '12') {
          qualifierDescription = 'Terms discount due date';
        } else if (qualifier === '167') {
          qualifierDescription = 'Charge payment due date';
        } else if (qualifier === '168') {
          qualifierDescription = 'Calculation date/time';
        } else if (qualifier === '169') {
          qualifierDescription = 'Lead time';
        } else if (qualifier === '227') {
          qualifierDescription = 'Beneficiary\'s banks due date';
        } else if (qualifier === '48') {
          qualifierDescription = 'Date of availability';
        } else if (qualifier === '206') {
          qualifierDescription = 'End date';
        } else if (qualifier === '334') {
          qualifierDescription = 'Status change date/time';
        } else if (qualifier === '146') {
          qualifierDescription = 'Availability for collection, date';
        }
      } else if (segmentId === 'PAC' && position === 1) {
        // Handle PAC qualifiers
        qualifierDescription = 'Number of packages';
      } else if (segmentId === 'PAC' && position === 3) {
        // Handle PAC package type qualifiers
        if (qualifier === 'PK') {
          qualifierDescription = 'Package';
        } else if (qualifier === 'CT') {
          qualifierDescription = 'Carton';
        } else if (qualifier === 'CS') {
          qualifierDescription = 'Case';
        } else if (qualifier === 'PL') {
          qualifierDescription = 'Pallet';
        } else if (qualifier === 'BX') {
          qualifierDescription = 'Box';
        } else if (qualifier === 'CR') {
          qualifierDescription = 'Crate';
        } else if (qualifier === 'PU') {
          qualifierDescription = 'Tray/tray pack';
        } else if (qualifier === 'SW') {
          qualifierDescription = 'Shrink wrapped';
        } else if (qualifier === 'RL') {
          qualifierDescription = 'Reel';
        }
      } else if (segmentId === 'LIN' && position === 1) {
        // Handle LIN qualifiers
        qualifierDescription = 'Line item number';
      } else if (segmentId === 'QTY' && position === 1) {
        // Handle QTY qualifiers
        if (qualifier === '12') {
          qualifierDescription = 'Despatch quantity';
        } else if (qualifier === '21') {
          qualifierDescription = 'Ordered quantity';
        } else if (qualifier === '46') {
          qualifierDescription = 'Delivered quantity';
        } else if (qualifier === '52') {
          qualifierDescription = 'Quantity per pack';
        } else if (qualifier === '59') {
          qualifierDescription = 'Number of consumer units in the traded unit';
        } else if (qualifier === '1') {
          qualifierDescription = 'Discrete quantity';
        } else if (qualifier === '11') {
          qualifierDescription = 'Split quantity';
        } else if (qualifier === '17') {
          qualifierDescription = 'Quantity on hand';
        } else if (qualifier === '40') {
          qualifierDescription = 'Normal delivery';
        } else if (qualifier === '79') {
          qualifierDescription = 'Previous quantity';
        } else if (qualifier === '113') {
          qualifierDescription = 'Quantity shipped to date';
        } else if (qualifier === '117') {
          qualifierDescription = 'Quantity on pallet';
        } else if (qualifier === '131') {
          qualifierDescription = 'Total delivery';
        }
      } else if (segmentId === 'NAD' && position === 1) {
        // Handle NAD qualifiers
        if (qualifier === 'BY') {
          qualifierDescription = 'Buyer - Party to whom merchandise is sold';
        } else if (qualifier === 'DP') {
          qualifierDescription = 'Delivery party - Party to which goods are to be delivered';
        } else if (qualifier === 'SU') {
          qualifierDescription = 'Supplier - Party who supplies goods and/or services';
        } else if (qualifier === 'SF') {
          qualifierDescription = 'Ship from - Party from where the goods will be or have been shipped';
        } else if (qualifier === 'ST') {
          qualifierDescription = 'Ship to - Party receiving the goods';
        } else if (qualifier === 'MF') {
          qualifierDescription = 'Manufacturer - Party who manufactures the goods';
        } else if (qualifier === 'IV') {
          qualifierDescription = 'Invoicee - Party to whom an invoice is issued';
        } else if (qualifier === 'UC') {
          qualifierDescription = 'Ultimate consignee - Final recipient of the goods';
        } else if (qualifier === 'CN') {
          qualifierDescription = 'Consignee - Party to which goods are consigned';
        } else if (qualifier === 'PE') {
          qualifierDescription = 'Payee - Party to whom payment should be made';
        } else if (qualifier === 'WH') {
          qualifierDescription = 'Warehouse keeper - Party taking responsibility for goods entered into a warehouse';
        } else if (qualifier === 'BS') {
          qualifierDescription = 'Bill and ship to - Party receiving goods and invoice';
        } else if (qualifier === 'SCO') {
          qualifierDescription = 'Service counter - Contact for customer service';
        }
      } else if (segmentId === 'RFF' && position === 1) {
        // Handle RFF qualifiers (reference segment)
        if (qualifier === 'CR') {
          qualifierDescription = 'Customer reference - Reference assigned by the customer';
        } else if (qualifier === 'ON') {
          qualifierDescription = 'Order number - Reference number assigned by the buyer to identify an order';
        } else if (qualifier === 'VN') {
          qualifierDescription = 'Vendor reference number - Reference number assigned by vendor';
        } else if (qualifier === 'CT') {
          qualifierDescription = 'Contract number - Reference number of a contract concluded between parties';
        } else if (qualifier === 'CN') {
          qualifierDescription = 'Consignee reference number';
        } else if (qualifier === 'AAK') {
          qualifierDescription = 'Despatch advice number';
        } else if (qualifier === 'IV') {
          qualifierDescription = 'Invoice number';
        } else if (qualifier === 'PO') {
          qualifierDescription = 'Purchase order reference number';
        } else if (qualifier === 'SI') {
          qualifierDescription = 'Shipment reference number';
        } else if (qualifier === 'AAJ') {
          qualifierDescription = 'Delivery order number';
        } else if (qualifier === 'ALO') {
          qualifierDescription = 'Receiving advice number';
        } else if (qualifier === 'DQ') {
          qualifierDescription = 'Delivery note number';
        } else if (qualifier === 'XA') {
          qualifierDescription = 'Company/place registration number';
        } else if (qualifier === 'ABO') {
          qualifierDescription = 'Originator\'s reference';
        } else if (qualifier === 'AES') {
          qualifierDescription = 'Transportation reference number';
        }
      } else if (segmentId === 'TDT' && position === 1) {
        // Handle TDT qualifiers
        if (qualifier === '20') {
          qualifierDescription = 'Main transport - Main carriage transport';
        } else if (qualifier === '10') {
          qualifierDescription = 'Pre-carriage transport';
        } else if (qualifier === '30') {
          qualifierDescription = 'On-carriage transport';
        }
      } else if (segmentId === 'CPS' && position === 1) {
        // Handle CPS qualifiers
        qualifierDescription = 'Consignment packing sequence number';
      } else if (segmentId === 'PAC' && position === 2) {
        // Handle PAC type qualifiers
        if (qualifier === '1') {
          qualifierDescription = 'Inner packages';
        } else if (qualifier === '2') {
          qualifierDescription = 'Intermediate packages';
        } else if (qualifier === '3') {
          qualifierDescription = 'Outer packages';
        } else if (qualifier === '4') {
          qualifierDescription = 'Transport packages';
        }
      } else if (segmentId === 'ALI' && position === 1) {
        // Handle ALI qualifiers
        qualifierDescription = 'Country of origin code';
      } else if (segmentId === 'BGM' && position === 1) {
        // Handle BGM qualifiers
        if (qualifier === '351') {
          qualifierDescription = 'Despatch advice - Document by means of which the seller or consignor informs the consignee about the despatch of goods';
        } else if (qualifier === '220') {
          qualifierDescription = 'Order - Document by means of which a buyer initiates a transaction with a seller';
        } else if (qualifier === '380') {
          qualifierDescription = 'Commercial invoice - Document claiming payment for goods or services supplied';
        } else if (qualifier === '310') {
          qualifierDescription = 'Offer/quotation - Document by means of which a seller offers goods or services';
        } else if (qualifier === '315') {
          qualifierDescription = 'Contract - Document evidencing an agreement';
        } else if (qualifier === '270') {
          qualifierDescription = 'Delivery instructions - Document by means of which a buyer gives instructions regarding the details of requested delivery of goods';
        } else if (qualifier === '231') {
          qualifierDescription = 'Purchase order response - Document provides the response to a purchase order';
        } else if (qualifier === '481') {
          qualifierDescription = 'Remittance advice - Document advising of a remittance of payment';
        }
      } else if (segmentId === 'UNH' && position === 2) {
        // Handle UNH message type qualifiers
        if (qualifier === 'DESADV') {
          qualifierDescription = 'Despatch advice message';
        } else if (qualifier === 'ORDERS') {
          qualifierDescription = 'Purchase order message';
        } else if (qualifier === 'INVOIC') {
          qualifierDescription = 'Invoice message';
        } else if (qualifier === 'QUOTES') {
          qualifierDescription = 'Quote message';
        } else if (qualifier === 'ORDRSP') {
          qualifierDescription = 'Purchase order response message';
        } else if (qualifier === 'INSDES') {
          qualifierDescription = 'Instruction for despatch message';
        } else if (qualifier === 'REMADV') {
          qualifierDescription = 'Remittance advice message';
        } else if (qualifier === 'RECADV') {
          qualifierDescription = 'Receiving advice message';
        }
      } else if (segmentId === 'UNS' && position === 1) {
        // Handle UNS section identifier
        if (qualifier === 'S') {
          qualifierDescription = 'Detail section';
        } else if (qualifier === 'D') {
          qualifierDescription = 'Detail section';
        } else if (qualifier === 'S') {
          qualifierDescription = 'Summary section';
        }
      } else if (segmentId === 'FTX' && position === 1) {
        // Handle FTX qualifiers
        if (qualifier === 'AAA') {
          qualifierDescription = 'Description of goods';
        } else if (qualifier === 'ALL') {
          qualifierDescription = 'General information';
        } else if (qualifier === 'INV') {
          qualifierDescription = 'Invoice information';
        } else if (qualifier === 'DEL') {
          qualifierDescription = 'Delivery information';
        } else if (qualifier === 'PKG') {
          qualifierDescription = 'Packaging information';
        } else if (qualifier === 'TRA') {
          qualifierDescription = 'Transportation information';
        } else if (qualifier === 'AAI') {
          qualifierDescription = 'General terms and conditions';
        } else if (qualifier === 'AAB') {
          qualifierDescription = 'Terms of payment';
        } else if (qualifier === 'PAI') {
          qualifierDescription = 'Payment instructions';
        } else if (qualifier === 'SUR') {
          qualifierDescription = 'Supplier remarks';
        } else if (qualifier === 'NAI') {
          qualifierDescription = 'Additional information';
        } else if (qualifier === 'HAZ') {
          qualifierDescription = 'Hazardous material information';
        } else if (qualifier === 'DIN') {
          qualifierDescription = 'Delivery instructions';
        }
      } else if (segmentId === 'LOC' && position === 1) {
        // Handle LOC qualifiers
        if (qualifier === '7') {
          qualifierDescription = 'Place of delivery';
        } else if (qualifier === '10') {
          qualifierDescription = 'Place of final destination';
        } else if (qualifier === '17') {
          qualifierDescription = 'Border crossing place';
        } else if (qualifier === '35') {
          qualifierDescription = 'Country of origin';
        } else if (qualifier === '5') {
          qualifierDescription = 'Place of departure';
        } else if (qualifier === '9') {
          qualifierDescription = 'Place/port of loading';
        } else if (qualifier === '11') {
          qualifierDescription = 'Place/port of discharge';
        } else if (qualifier === '28') {
          qualifierDescription = 'Country of first destination';
        } else if (qualifier === '83') {
          qualifierDescription = 'Place of transshipment';
        }
      } else if (segmentId === 'GIN' && position === 1) {
        // Handle GIN qualifiers
        if (qualifier === 'BN') {
          qualifierDescription = 'Serial number';
        } else if (qualifier === 'BX') {
          qualifierDescription = 'Batch number';
        } else if (qualifier === 'SRV') {
          qualifierDescription = 'Service number';
        } else if (qualifier === 'PV') {
          qualifierDescription = 'Promotional variant number';
        }
      } else if (segmentId === 'PCI' && position === 1) {
        // Handle PCI (Package Identification) qualifiers
        if (qualifier === '14') {
          qualifierDescription = 'Product marking';
        } else if (qualifier === '33') {
          qualifierDescription = 'Marking';
        } else if (qualifier === '17') {
          qualifierDescription = 'One-dimensional bar-coded label';
        } else if (qualifier === '18') {
          qualifierDescription = 'Two-dimensional bar-coded label';
        }
      } else if (segmentId === 'MEA' && position === 1) {
        // Handle MEA (Measurements) qualifiers
        if (qualifier === 'PD') {
          qualifierDescription = 'Physical dimensions';
        } else if (qualifier === 'WT') {
          qualifierDescription = 'Weight';
        } else if (qualifier === 'VOL') {
          qualifierDescription = 'Volume';
        } else if (qualifier === 'AAE') {
          qualifierDescription = 'Measurement';
        }
      } else if (segmentId === 'MOA' && position === 1) {
        // Handle MOA (Monetary Amount) qualifiers
        if (qualifier === '86') {
          qualifierDescription = 'Message total - Total value of the message';
        } else if (qualifier === '79') {
          qualifierDescription = 'Total line items amount';
        } else if (qualifier === '129') {
          qualifierDescription = 'Total amount subject to payment discount';
        } else if (qualifier === '9') {
          qualifierDescription = 'Amount due/amount payable';
        } else if (qualifier === '125') {
          qualifierDescription = 'Taxable amount';
        } else if (qualifier === '176') {
          qualifierDescription = 'Message total duty/tax/fee amount';
        } else if (qualifier === '131') {
          qualifierDescription = 'Total charges/allowances';
        }
      }
    }
    
    if (qualifierDescription) {
      return ` - ${qualifierDescription}`;
    }
    
    return '';
  };
  
  // Highlight segments with syntax coloring and detailed descriptions
  const highlightSegments = (text) => {
    if (!text) return '';
    
    const lines = text.split('\n');
    
    return lines.map((line, index) => {
      // Parse the segment data
      const { segmentId, qualifiers, composites } = parseSegmentData(line);
      
      // Check if this segment has validation errors
      const segmentErrors = errorMap.get(index) || [];
      
      
      // Check for errors vs warnings using our explicit classification
      const hasErrors = segmentErrors.some(issue => issue.issueType === 'error');
      const hasWarnings = segmentErrors.some(issue => issue.issueType === 'warning');
      
      // Get the basic segment description
      const segmentDesc = getSegmentDescription(segmentId);
      
      // Create a more detailed description with error information
      let description = ` /* ${segmentDesc}`;
      
      // Add error information if present
      if (segmentErrors.length > 0) {
        description += ` - ISSUES: ${segmentErrors.map(issue => issue.message || issue.text).join('; ')}`;
      }
      
      // Add qualifier descriptions if available
      if (qualifiers.length > 0) {
        // Add details for the first qualifier if it exists
        const firstQualifier = qualifiers[0];
        if (firstQualifier) {
          const qualifierDesc = getQualifierDescription(segmentId, firstQualifier, 1);
          if (qualifierDesc) {
            description += ` - Qualifier ${firstQualifier}${qualifierDesc}`;
          }
          
          // For special segments like UNB, provide more details about the composite elements
          if (segmentId === 'UNB' && composites.length > 0) {
            // UNB segment with composite elements like UNB+UNOC:3+7371...
            if (composites[0].length > 1) {
              const syntaxId = composites[0][0];
              const syntaxVersion = composites[0][1];
              description = ` /* Interchange Header - Syntax identifier ${syntaxId} version ${syntaxVersion} */`;
            }
            
            // Add sender/recipient details if available
            if (composites.length >= 2 && composites[1].length > 0) {
              const senderId = composites[1][0];
              description += ` - Sender: ${senderId}`;
            }
            
            if (composites.length >= 3 && composites[2].length > 0) {
              const recipientId = composites[2][0];
              description += ` - Recipient: ${recipientId}`;
            }
          } else if (segmentId === 'DTM' && composites.length > 0 && composites[0].length > 2) {
            // DTM segment with format qualifiers DTM+137:20250403:102'
            const dateValue = composites[0][1] || '';
            const formatCode = composites[0][2] || '';
            let formattedDate = dateValue;
            
            // Format the date based on the format code
            if (formatCode === '102' && dateValue.length === 8) {
              // CCYYMMDD format
              formattedDate = `${dateValue.substring(0, 4)}-${dateValue.substring(4, 6)}-${dateValue.substring(6, 8)}`;
            } else if (formatCode === '203' && dateValue.length >= 10) {
              // CCYYMMDDHHMM format
              formattedDate = `${dateValue.substring(0, 4)}-${dateValue.substring(4, 6)}-${dateValue.substring(6, 8)} ${dateValue.substring(8, 10)}:${dateValue.substring(10, 12)}`;
            }
            
            description += ` - Date: ${formattedDate}`;
          }
        }
      }
      
      // Add special detailed explanation for specific segment types
      if (segmentId === 'UNA') {
        description = ` /* Service String Advice - Defines special characters used in the interchange: component separator(:), element separator(+), decimal point(.), escape char(?), reserved( ), segment terminator(') */`;
      } else if (segmentId === 'UNB') {
        description = ` /* Interchange Header - Identifies the sender and recipient of the interchange */`;
        
        // Extract and format UNB details if available
        if (composites.length >= 5) {
          let sender = composites[1] && composites[1][0] ? composites[1][0] : '';
          let recipient = composites[2] && composites[2][0] ? composites[2][0] : '';
          let dateTime = '';
          
          if (composites[3] && composites[3].length >= 2) {
            const date = composites[3][0];
            const time = composites[3][1];
            if (date && date.length >= 6) {
              dateTime = `20${date.substring(0, 2)}-${date.substring(2, 4)}-${date.substring(4, 6)}`;
              
              if (time && time.length >= 4) {
                dateTime += ` ${time.substring(0, 2)}:${time.substring(2, 4)}`;
              }
            }
          }
          
          let reference = composites[4] && composites[4][0] ? composites[4][0] : '';
          
          description = ` /* Interchange Header - From: ${sender} To: ${recipient}`;
          if (dateTime) description += ` Date: ${dateTime}`;
          if (reference) description += ` Reference: ${reference}`;
          description += ` */`;
        }
      } else if (segmentId === 'UNZ') {
        // UNZ segment with control count and reference
        let count = qualifiers.length > 0 ? qualifiers[0] : '';
        let reference = qualifiers.length > 1 ? qualifiers[1] : '';
        
        description = ` /* Interchange Trailer - End of interchange`;
        if (count) description += ` - ${count} message(s)`;
        if (reference) description += ` - Reference: ${reference}`;
        description += ` */`;
      } else if (segmentId === 'UNT') {
        // UNT segment with segment count and reference
        let count = qualifiers.length > 0 ? qualifiers[0] : '';
        let reference = qualifiers.length > 1 ? qualifiers[1] : '';
        
        description = ` /* Message Trailer - End of message`;
        if (count) description += ` - ${count} segment(s)`;
        if (reference) description += ` - Reference: ${reference}`;
        description += ` */`;
      } else if (segmentId === 'UNH') {
        description = ` /* Message Header - Identifies a message within a transmission */`;
        
        // Extract and format UNH details if available
        if (composites.length >= 2) {
          let reference = composites[0] && composites[0][0] ? composites[0][0] : '';
          let messageType = '';
          let messageVersion = '';
          
          if (composites[1] && composites[1].length >= 3) {
            messageType = composites[1][0] || '';
            messageVersion = `${composites[1][1] || ''}:${composites[1][2] || ''}`;
          }
          
          description = ` /* Message Header - Reference: ${reference}`;
          if (messageType) {
            if (messageType === 'DESADV') {
              description += ` Type: Despatch Advice (${messageType})`;
            } else if (messageType === 'ORDERS') {
              description += ` Type: Purchase Order (${messageType})`;
            } else if (messageType === 'INVOIC') {
              description += ` Type: Invoice (${messageType})`;
            } else {
              description += ` Type: ${messageType}`;
            }
          }
          if (messageVersion) description += ` Version: ${messageVersion}`;
          description += ` */`;
        }
      } else if (segmentId === 'BGM') {
        // Add more context to BGM segment (Beginning of Message)
        if (qualifiers.length > 0) {
          let docType = '';
          let docNumber = '';
          let docFunction = '';
          
          if (qualifiers[0] === '351') {
            docType = 'Dispatch advice (shipping notice)';
          } else if (qualifiers[0] === '220') {
            docType = 'Order';
          } else if (qualifiers[0] === '380') {
            docType = 'Commercial invoice';
          } else {
            docType = qualifiers[0];
          }
          
          if (qualifiers.length > 1) {
            docNumber = qualifiers[1];
          }
          
          if (qualifiers.length > 2) {
            if (qualifiers[2] === '9') {
              docFunction = 'Original';
            } else if (qualifiers[2] === '7') {
              docFunction = 'Duplicate';
            } else if (qualifiers[2] === '5') {
              docFunction = 'Replace';
            } else {
              docFunction = qualifiers[2];
            }
          }
          
          description = ` /* Beginning of Message - ${docType}`;
          if (docNumber) description += ` - Document number: ${docNumber}`;
          if (docFunction) description += ` - Function: ${docFunction}`;
          description += ` */`;
        }
      } else if (segmentId === 'DTM' && qualifiers.length > 0) {
        // Add more context to DTM segment
        let dateType = '';
        let dateValue = '';
        let formatCode = '';
        
        if (qualifiers[0] === '137') {
          dateType = 'Document/message date/time';
        } else if (qualifiers[0] === '11') {
          dateType = 'Despatch date and time';
        } else if (qualifiers[0] === '3') {
          dateType = 'Invoice date/time';
        } else if (qualifiers[0] === '35') {
          dateType = 'Delivery date/time, actual';
        } else if (qualifiers[0] === '4') {
          dateType = 'Order date/time';
        } else if (qualifiers[0] === '7') {
          dateType = 'Effective date/time';
        } else if (qualifiers[0] === '10') {
          dateType = 'Shipment date/time, requested';
        } else if (qualifiers[0] === '17') {
          dateType = 'Delivery date/time, estimated';
        } else if (qualifiers[0] === '171') {
          dateType = 'Reference date/time';
        } else if (qualifiers[0] === '2') {
          dateType = 'Delivery date/time, requested';
        } else {
          dateType = qualifiers[0];
        }
        
        if (composites.length > 0 && composites[0].length > 1) {
          dateValue = composites[0][1];
          if (composites[0].length > 2) {
            formatCode = composites[0][2];
          }
        }
        
        let formattedDate = dateValue;
        if (formatCode === '102' && dateValue && dateValue.length === 8) {
          // CCYYMMDD format
          formattedDate = `${dateValue.substring(0, 4)}-${dateValue.substring(4, 6)}-${dateValue.substring(6, 8)}`;
        } else if (formatCode === '203' && dateValue && dateValue.length >= 10) {
          // CCYYMMDDHHMM format
          formattedDate = `${dateValue.substring(0, 4)}-${dateValue.substring(4, 6)}-${dateValue.substring(6, 8)} ${dateValue.substring(8, 10)}:${dateValue.substring(10, 12)}`;
        } else if (formatCode === '101' && dateValue && dateValue.length === 6) {
          // YYMMDD format
          formattedDate = `20${dateValue.substring(0, 2)}-${dateValue.substring(2, 4)}-${dateValue.substring(4, 6)}`;
        } else if (formatCode === '2' && dateValue) {
          // MMDD format
          formattedDate = `MM-DD: ${dateValue.substring(0, 2)}-${dateValue.substring(2, 4)}`;
        } else if (formatCode === '3' && dateValue && dateValue.length === 6) {
          // HHMMSS format
          formattedDate = `${dateValue.substring(0, 2)}:${dateValue.substring(2, 4)}:${dateValue.substring(4, 6)}`;
        }
        
        description = ` /* Date/Time/Period - ${dateType}`;
        if (formattedDate) description += ` - ${formattedDate}`;
        if (formatCode) {
          if (formatCode === '102') {
            description += ` (CCYYMMDD format)`;
          } else if (formatCode === '203') {
            description += ` (CCYYMMDDHHMM format)`;
          } else if (formatCode === '101') {
            description += ` (YYMMDD format)`;
          } else if (formatCode === '2') {
            description += ` (MMDD format)`;
          } else if (formatCode === '3') {
            description += ` (HHMMSS format)`;
          } else {
            description += ` (Format code: ${formatCode})`;
          }
        }
        description += ` */`;
      } else if (segmentId === 'NAD' && qualifiers.length > 0) {
        // Add more context to NAD segment
        let partyType = '';
        let partyName = '';
        let partyId = '';
        
        if (qualifiers[0] === 'BY') {
          partyType = 'Buyer';
        } else if (qualifiers[0] === 'DP') {
          partyType = 'Delivery Party';
        } else if (qualifiers[0] === 'SU') {
          partyType = 'Supplier';
        } else {
          partyType = qualifiers[0];
        }
        
        description = ` /* Name and Address - ${partyType}`;
        
        // Extract party details if available
        if (composites.length > 1 && composites[1] && composites[1][0]) {
          partyName = composites[1][0];
          if (partyName) description += ` - ${partyName}`;
        }
        
        // Look for EAN/GLN number in party ID field
        if (composites.length > 2 && composites[2] && composites[2].length > 0) {
          partyId = composites[2][0];
          const idQualifier = composites[2].length > 1 ? composites[2][1] : '';
          
          if (partyId) {
            if (idQualifier === '9') {
              description += ` - EAN: ${partyId}`;
            } else if (idQualifier === '91') {
              description += ` - Assigned by seller: ${partyId}`;
            } else if (idQualifier === '92') {
              description += ` - Assigned by buyer: ${partyId}`;
            } else {
              description += ` - ID: ${partyId}`;
            }
          }
        }
        
        // Look for address details
        if (composites.length > 4) {
          const street = composites[3] && composites[3][0] ? composites[3][0] : '';
          const city = composites[4] && composites[4][0] ? composites[4][0] : '';
          const postcode = composites[5] && composites[5][0] ? composites[5][0] : '';
          const countryCode = composites[8] && composites[8][0] ? composites[8][0] : '';
          
          let address = '';
          if (street) address += ` ${street}`;
          if (city) address += ` ${city}`;
          if (postcode) address += ` ${postcode}`;
          if (countryCode) address += ` ${countryCode}`;
          
          if (address) {
            description += ` - Address:${address}`;
          }
        }
        
        description += ` */`;
      } else if (segmentId === 'LIN') {
        // Extract line number and item details if available
        let lineNumber = qualifiers.length > 0 ? qualifiers[0] : '';
        let itemId = '';
        let itemType = '';
        
        if (composites.length > 2 && composites[2] && composites[2].length > 0) {
          itemId = composites[2][0] || '';
          itemType = composites[2].length > 1 ? composites[2][1] : '';
        }
        
        description = ` /* Line Item - Line #${lineNumber}`;
        if (itemId) {
          if (itemType === 'EN') {
            description += ` - EAN: ${itemId}`;
          } else if (itemType === 'SA') {
            description += ` - Supplier article no: ${itemId}`;
          } else if (itemType === 'BP') {
            description += ` - Buyer article no: ${itemId}`;
          } else {
            description += ` - Item ID: ${itemId}`;
          }
        }
        description += ` */`;
      } else if (segmentId === 'PAC') {
        // Extract package details
        let packageCount = qualifiers.length > 0 ? qualifiers[0] : '';
        let packageType = '';
        
        if (composites.length > 2 && composites[2] && composites[2].length > 0) {
          packageType = composites[2][0] || '';
        }
        
        description = ` /* Package - ${packageCount} package(s)`;
        if (packageType) {
          if (packageType === 'PK') {
            description += ` - Type: Package`;
          } else if (packageType === 'CT') {
            description += ` - Type: Carton`;
          } else if (packageType === 'CS') {
            description += ` - Type: Case`;
          } else if (packageType === 'PL') {
            description += ` - Type: Pallet`;
          } else {
            description += ` - Type: ${packageType}`;
          }
        }
        description += ` */`;
      } else if (segmentId === 'QTY' && qualifiers.length > 0 && composites.length > 0) {
        // Extract quantity details
        let qtyType = '';
        let qtyValue = '';
        let unitCode = '';
        
        if (qualifiers[0] === '12') {
          qtyType = 'Despatch quantity';
        } else if (qualifiers[0] === '21') {
          qtyType = 'Ordered quantity';
        } else {
          qtyType = qualifiers[0];
        }
        
        if (composites[0] && composites[0].length > 1) {
          qtyValue = composites[0][1] || '';
          unitCode = composites[0].length > 2 ? composites[0][2] : '';
        }
        
        description = ` /* Quantity - ${qtyType}: ${qtyValue}`;
        if (unitCode) {
          if (unitCode === 'PCE') {
            description += ` pieces`;
          } else if (unitCode === 'KGM') {
            description += ` kilograms`;
          } else if (unitCode === 'LTR') {
            description += ` liters`;
          } else if (unitCode === 'MTR') {
            description += ` meters`;
          } else {
            description += ` ${unitCode}`;
          }
        }
        description += ` */`;
      } else if (segmentId === 'RFF' && qualifiers.length > 0 && composites.length > 0) {
        // Extract reference details
        let refType = '';
        let refValue = '';
        
        if (qualifiers[0] === 'ON') {
          refType = 'Order number';
        } else if (qualifiers[0] === 'CR') {
          refType = 'Customer reference number';
        } else if (qualifiers[0] === 'VN') {
          refType = 'Vendor reference number';
        } else {
          refType = qualifiers[0];
        }
        
        if (composites[0] && composites[0].length > 1) {
          refValue = composites[0][1] || '';
        }
        
        description = ` /* Reference - ${refType}: ${refValue} */`;
      } else if (segmentId === 'GIN') {
        // Extract identity number details
        let identityType = '';
        if (qualifiers.length > 0) {
          if (qualifiers[0] === 'BN') {
            identityType = 'Serial number';
          } else if (qualifiers[0] === 'BX') {
            identityType = 'Batch number';
          } else {
            identityType = qualifiers[0];
          }
        }
        
        let identityValues = [];
        for (let i = 1; i < composites.length; i++) {
          if (composites[i] && composites[i].length > 0) {
            identityValues.push(composites[i][0]);
          }
        }
        
        description = ` /* Goods Identity Number - ${identityType}`;
        if (identityValues.length > 0) {
          description += `: ${identityValues.join(', ')}`;
        }
        description += ` */`;
      } else if (segmentId === 'PCI') {
        // Extract package marking details
        let markingType = '';
        let markingValue = '';
        
        if (qualifiers.length > 0) {
          if (qualifiers[0] === '14') {
            markingType = 'Product marking';
          } else if (qualifiers[0] === '33') {
            markingType = 'Marking';
          } else if (qualifiers[0] === '17') {
            markingType = 'One-dimensional bar-coded label';
          } else {
            markingType = qualifiers[0];
          }
        }
        
        if (composites.length > 1 && composites[1] && composites[1].length > 0) {
          markingValue = composites[1][0];
        }
        
        description = ` /* Package Identification - ${markingType}`;
        if (markingValue) {
          description += `: ${markingValue}`;
        }
        description += ` */`;
      } else if (segmentId === 'MEA') {
        // Extract measurement details
        let measureType = '';
        let measureValue = '';
        let unitCode = '';
        
        if (qualifiers.length > 0) {
          if (qualifiers[0] === 'PD') {
            measureType = 'Physical dimensions';
          } else if (qualifiers[0] === 'WT') {
            measureType = 'Weight';
          } else if (qualifiers[0] === 'VOL') {
            measureType = 'Volume';
          } else {
            measureType = qualifiers[0];
          }
        }
        
        if (composites.length > 2 && composites[2] && composites[2].length > 1) {
          measureValue = composites[2][1];
          unitCode = composites[2].length > 2 ? composites[2][2] : '';
        }
        
        description = ` /* Measurements - ${measureType}`;
        if (measureValue) {
          description += `: ${measureValue}`;
          if (unitCode) {
            if (unitCode === 'KGM') {
              description += ` kilograms`;
            } else if (unitCode === 'LTR') {
              description += ` liters`;
            } else if (unitCode === 'MTR') {
              description += ` meters`;
            } else if (unitCode === 'CMT') {
              description += ` centimeters`;
            } else if (unitCode === 'MMT') {
              description += ` millimeters`;
            } else {
              description += ` ${unitCode}`;
            }
          }
        }
        description += ` */`;
      } else if (segmentId === 'CNT') {
        // Extract control total details
        let controlType = '';
        let controlValue = '';
        
        if (qualifiers.length > 0 && composites.length > 0 && composites[0].length > 1) {
          controlType = composites[0][0];
          controlValue = composites[0][1];
          
          if (controlType === '2') {
            controlType = 'Number of line items';
          } else if (controlType === '7') {
            controlType = 'Total gross weight';
          } else if (controlType === '11') {
            controlType = 'Total number of packages';
          }
        }
        
        description = ` /* Control Total - ${controlType}: ${controlValue} */`;
      } else if (segmentId === 'FTX') {
        // Extract free text details
        let textType = '';
        let textValue = '';
        
        if (qualifiers.length > 0) {
          if (qualifiers[0] === 'AAA') {
            textType = 'Description of goods';
          } else if (qualifiers[0] === 'ALL') {
            textType = 'General information';
          } else if (qualifiers[0] === 'DEL') {
            textType = 'Delivery information';
          } else {
            textType = qualifiers[0];
          }
        }
        
        if (composites.length > 4 && composites[4] && composites[4].length > 0) {
          textValue = composites[4][0];
        }
        
        description = ` /* Free Text - ${textType}`;
        if (textValue) {
          description += `: ${textValue}`;
        }
        description += ` */`;
      } else if (segmentId === 'CPS') {
        // Extract consignment packing sequence details
        let sequenceNumber = qualifiers.length > 0 ? qualifiers[0] : '';
        let nestedLevel = qualifiers.length > 1 ? qualifiers[1] : '';
        
        description = ` /* Consignment Packing Sequence - ${sequenceNumber}`;
        if (nestedLevel) {
          description += ` (Nested level: ${nestedLevel})`;
        }
        description += ` */`;
      } else if (segmentId === 'TOD') {
        // Extract terms of delivery details
        let termCode = '';
        let termLocation = '';
        
        if (composites.length > 3 && composites[3] && composites[3].length > 0) {
          termCode = composites[3][0];
          
          if (termCode === 'CIF') {
            termCode = 'Cost, insurance and freight';
          } else if (termCode === 'FOB') {
            termCode = 'Free on board';
          } else if (termCode === 'EXW') {
            termCode = 'Ex works';
          } else if (termCode === 'DDP') {
            termCode = 'Delivered duty paid';
          }
        }
        
        description = ` /* Terms of Delivery - `;
        if (termCode) {
          description += termCode;
        }
        if (termLocation) {
          description += ` (${termLocation})`;
        }
        description += ` */`;
      }
      
      if (!description.endsWith(' */')) {
        description += ' */';
      }
      
      // Create tooltip content for errors/warnings
      const tooltipContent = segmentErrors.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {hasErrors ? '🚨 Error Details' : '⚠️ Warning Details'}
          </Typography>
          {segmentErrors.map((issue, i) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ 
                color: 'white',
                fontWeight: 'medium',
                mb: 0.5
              }}>
                • {issue.message || issue.text}
              </Typography>
              
              {/* Enhanced contextual information */}
              {(() => {
                // Enhance error messages with contextual information based on segment and error type
                if (issue.type === 'elements' && issue.message && issue.message.includes('empty required element at position')) {
                  const positionMatch = issue.message.match(/position (\d+)/);
                  if (positionMatch && segmentId === 'NAD') {
                    const position = parseInt(positionMatch[1]);
                    let elementDescription = '';
                    
                    switch (position) {
                      case 1:
                        elementDescription = 'Party Qualifier (e.g., BY, SU, DP)';
                        break;
                      case 2:
                        elementDescription = 'Party Identification (GLN/ID number)';
                        break;
                      case 3:
                        elementDescription = 'Name and Address Details';
                        break;
                      case 4:
                        elementDescription = 'Party Name';
                        break;
                      default:
                        elementDescription = `Element ${position}`;
                    }
                    
                    return (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', ml: 1, fontStyle: 'italic' }}>
                        💡 Position {position}: {elementDescription}
                      </Typography>
                    );
                  } else if (positionMatch) {
                    const position = parseInt(positionMatch[1]);
                    return (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', ml: 1, fontStyle: 'italic' }}>
                        💡 Missing required element at position {position}
                      </Typography>
                    );
                  }
                }
                
                // Show standard additional information
                return (
                  <>
                    {issue.type && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', ml: 1 }}>
                        Type: {issue.type}
                      </Typography>
                    )}
                    
                    {issue.missing && Array.isArray(issue.missing) && issue.missing.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', ml: 1 }}>
                        Missing: {issue.missing.join(', ')}
                      </Typography>
                    )}
                    
                    {issue.details && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', ml: 1 }}>
                        Details: {issue.details}
                      </Typography>
                    )}
                    
                    {issue.context && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', ml: 1 }}>
                        Context: {issue.context}
                      </Typography>
                    )}
                  </>
                );
              })()}
              
              {issue.locationDisplay && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', ml: 1, fontWeight: 'medium' }}>
                  📍 Line: {issue.locationDisplay}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      ) : null;

      const segmentBox = (
        <Box 
          component="div" 
          key={index} 
          sx={{ 
            mb: 1,
            // Add error highlighting
            backgroundColor: hasErrors ? 'rgba(244, 67, 54, 0.1)' : hasWarnings ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
            borderLeft: hasErrors ? '4px solid #f44336' : hasWarnings ? '4px solid #ff9800' : 'none',
            pl: hasErrors || hasWarnings ? 1 : 0,
            py: hasErrors || hasWarnings ? 0.5 : 0,
            transition: 'all 0.2s ease',
            cursor: segmentErrors.length > 0 ? 'help' : 'default',
            '&:hover': segmentErrors.length > 0 ? {
              backgroundColor: hasErrors ? 'rgba(244, 67, 54, 0.15)' : 'rgba(255, 152, 0, 0.15)',
              transform: 'translateX(2px)'
            } : {}
          }}
        >
          <Box component="span" sx={{ 
            color: hasErrors ? '#ff5722' : '#ff79c6', 
            fontWeight: 'bold',
            textShadow: hasErrors ? '0 0 2px #ff5722' : hasWarnings ? '0 0 2px #ff9800' : 'none'
          }}>
            {segmentId}
          </Box>
          <Box component="span" sx={{ color: '#f8f8f2' }}>{line.substring(segmentId.length)}</Box>
          <Box component="span" sx={{ 
            color: hasErrors ? '#ffab91' : hasWarnings ? '#ffcc80' : '#6272a4', 
            fontStyle: 'italic'
          }}>
            {description}
          </Box>
          {/* Error indicator */}
          {segmentErrors.length > 0 && (
            <Box component="span" sx={{ 
              color: hasErrors ? '#ff5722' : '#ff9800',
              fontWeight: 'bold',
              ml: 1
            }}>
              {hasErrors ? ' ⚠ ERROR' : ' ⚠ WARNING'}
            </Box>
          )}
        </Box>
      );

      // Wrap with tooltip if there are errors/warnings
      return segmentErrors.length > 0 ? (
        <Tooltip 
          key={index}
          title={tooltipContent}
          arrow
          placement="right"
          enterDelay={300}
          leaveDelay={100}
          sx={{
            '& .MuiTooltip-tooltip': {
              maxWidth: 400,
              backgroundColor: hasErrors ? 'rgba(244, 67, 54, 0.95)' : 'rgba(255, 152, 0, 0.95)',
              color: 'white',
              fontSize: '0.875rem',
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)'
            },
            '& .MuiTooltip-arrow': {
              color: hasErrors ? 'rgba(244, 67, 54, 0.95)' : 'rgba(255, 152, 0, 0.95)'
            }
          }}
        >
          {segmentBox}
        </Tooltip>
      ) : segmentBox;
    });
  };
  
  // Highlight elements within segments with detailed descriptions
  const highlightElements = (text) => {
    if (!text) return '';
    
    const lines = text.split('\n');
    let currentSegmentId = '';
    let elementPosition = 0;
    
    return lines.map((line, index) => {
      // Highlight different parts of the line
      if (line.startsWith('  +')) {
        // Element lines (indented with '+')
        elementPosition++;
        
        // Extract the element value (everything after the +)
        const elementValue = line.substring(3);
        
        // Handle composite elements (those with : separators)
        if (elementValue.includes(':')) {
          const compositeElements = elementValue.split(':');
          const elementDesc = getElementDescription(currentSegmentId, elementPosition);
          let mainDescription = '';
          
          if (elementDesc) {
            mainDescription = ` /* ${elementDesc.name} - ${elementDesc.description}`;
            
            // Add description for the first component if available
            if (compositeElements.length > 0 && compositeElements[0]) {
              const firstComponent = compositeElements[0];
              if (elementDesc.explanations && elementDesc.explanations[firstComponent]) {
                mainDescription += ` - ${elementDesc.explanations[firstComponent]}`;
              } else if (elementDesc.values && elementDesc.values[firstComponent]) {
                mainDescription += ` - ${elementDesc.values[firstComponent]}`;
              }
            }
            
            mainDescription += ' */';
          } else {
            // Special handling for specific segments
            if (currentSegmentId === 'DTM' && elementPosition === 1) {
              // DTM+137:202504031:102' date/time/period format
              if (compositeElements.length >= 2) {
                const qualifier = compositeElements[0];
                const format = compositeElements[2] || '';
                
                let qualifierDesc = '';
                if (qualifier === '137') {
                  qualifierDesc = 'Document/message date/time';
                } else if (qualifier === '11') {
                  qualifierDesc = 'Despatch date and or time';
                }
                
                let formatDesc = '';
                if (format === '102') {
                  formatDesc = ' - Format: CCYYMMDD';
                } else if (format === '203') {
                  formatDesc = ' - Format: CCYYMMDDHHMM';
                }
                
                mainDescription = ` /* Date/Time/Period Qualifier - ${qualifierDesc}${formatDesc} */`;
              }
            } else if (currentSegmentId === 'UNB' && elementPosition === 2) {
              // UNB sender identification (UNB+UNOC:3+7317150100004:14+...)
              mainDescription = ` /* Syntax Identifier - Character set encoding and version */`;
            } else if (currentSegmentId === 'UNB' && elementPosition === 3) {
              // UNB sender identification (UNB+UNOC:3+7317150100004:14+...)
              mainDescription = ` /* Sender Identification - Identifies the sender of the interchange */`;
            } else if (currentSegmentId === 'UNB' && elementPosition === 4) {
              // UNB recipient identification
              mainDescription = ` /* Recipient Identification - Identifies the recipient of the interchange */`;
            } else if (currentSegmentId === 'UNH' && elementPosition === 2) {
              // UNH message identifier (UNH+...+EANCOM:...)
              mainDescription = ` /* Message Identifier - Identification of the message type, version, release */`;
            }
          }
          
          return (
            <Box component="div" key={index}>
              <Box component="span" sx={{ color: '#f8f8f2' }}>  </Box>
              <Box component="span" sx={{ color: '#ff79c6' }}>+</Box>
              <Box component="span" sx={{ color: '#50fa7b' }}>{elementValue}</Box>
              <Box component="span" sx={{ color: '#6272a4', fontStyle: 'italic' }}>{mainDescription}</Box>
            </Box>
          );
        } else {
          // Simple elements (no composite)
          const elementDesc = getElementDescription(currentSegmentId, elementPosition);
          let description = '';
          
          if (elementDesc) {
            description = ` /* ${elementDesc.name} - ${elementDesc.description}`;
            
            // If this element has a meaningful value code explanation, add it
            if (elementValue && elementDesc.explanations && elementDesc.explanations[elementValue]) {
              description += ` - ${elementDesc.explanations[elementValue]}`;
            } else if (elementValue && elementDesc.values && elementDesc.values[elementValue]) {
              description += ` - ${elementDesc.values[elementValue]}`;
            }
            
            description += ' */';
          } else {
            // Special handling for specific segments that don't have standard element descriptions
            if (currentSegmentId === 'LIN' && elementPosition === 1) {
              // LIN+1 line number element
              description = ` /* Line Item Number - Sequential number of the line in the message */`;
            } else if (currentSegmentId === 'QTY' && elementPosition === 1) {
              // QTY+12 quantity qualifier
              if (elementValue === '12') {
                description = ` /* Quantity Qualifier - Despatch quantity */`;
              }
            } else if (currentSegmentId === 'NAD' && elementPosition === 1) {
              // NAD party qualifier
              if (elementValue === 'BY') {
                description = ` /* Party Qualifier - Buyer */`;
              } else if (elementValue === 'DP') {
                description = ` /* Party Qualifier - Delivery party */`;
              } else if (elementValue === 'SU') {
                description = ` /* Party Qualifier - Supplier */`;
              }
            } else if (currentSegmentId === 'RFF' && elementPosition === 1) {
              // RFF reference qualifier
              if (elementValue === 'ON') {
                description = ` /* Reference Qualifier - Order number */`;
              } else if (elementValue === 'CR') {
                description = ` /* Reference Qualifier - Customer reference */`;
              }
            } else if (currentSegmentId === 'PAC' && elementPosition === 1) {
              // PAC package count
              description = ` /* Number of packages */`;
            } else if (currentSegmentId === 'ALI' && elementPosition === 1) {
              // ALI country of origin code
              description = ` /* Country of origin code */`;
            }
          }
          
        return (
          <Box component="div" key={index}>
            <Box component="span" sx={{ color: '#f8f8f2' }}>  </Box>
            <Box component="span" sx={{ color: '#ff79c6' }}>+</Box>
              <Box component="span" sx={{ color: '#50fa7b' }}>{elementValue}</Box>
              <Box component="span" sx={{ color: '#6272a4', fontStyle: 'italic' }}>{description}</Box>
          </Box>
        );
        }
      } else if (line.indexOf('+') > 0) {
        // Segment identifier lines
        const segmentId = line.substring(0, line.indexOf('+'));
        currentSegmentId = segmentId;
        elementPosition = 0;
        
        const segmentDesc = getSegmentDescription(segmentId);
        const description = ` /* ${segmentDesc} */`;
        
        return (
          <Box component="div" key={index} sx={{ mb: 0.5 }}>
            <Box component="span" sx={{ color: '#ff79c6', fontWeight: 'bold' }}>{segmentId}</Box>
            <Box component="span" sx={{ color: '#ff79c6' }}>+</Box>
            <Box component="span" sx={{ color: '#f8f8f2' }}>{line.substring(segmentId.length + 1)}</Box>
            <Box component="span" sx={{ color: '#6272a4', fontStyle: 'italic' }}>{description}</Box>
          </Box>
        );
      } else {
        // Lines with segment terminators or other content
        if (line.length >= 3) {
          const segmentId = line.substring(0, 3);
          if (segmentId && !line.includes('+')) {
            currentSegmentId = segmentId;
            const segmentDesc = getSegmentDescription(segmentId);
            const description = ` /* ${segmentDesc} */`;
            
            return (
              <Box component="div" key={index}>
                <Box component="span" sx={{ color: '#ff79c6', fontWeight: 'bold' }}>{segmentId}</Box>
                <Box component="span" sx={{ color: '#f8f8f2' }}>{line.substring(3)}</Box>
                <Box component="span" sx={{ color: '#6272a4', fontStyle: 'italic' }}>{description}</Box>
              </Box>
            );
          }
        }
        
        return (
          <Box component="div" key={index}>
            <Box component="span" sx={{ color: '#f8f8f2' }}>{line}</Box>
          </Box>
        );
      }
    });
  };
  
  const handleFormatChange = (event, newFormat) => {
    if (newFormat !== null) {
      setFormatType(newFormat);
    }
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(content).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      () => {
        console.error('Failed to copy content');
      }
    );
  };
  
  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + 1, 24));
  };
  
  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - 1, 8));
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        sx={{
          mb: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          {/* Error legend */}
          {validationResults && (validationResults.errors?.length > 0 || validationResults.warnings?.length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Legend:
              </Typography>
              {validationResults.errors?.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    borderLeft: '3px solid #f44336',
                    borderRadius: '2px'
                  }} />
                  <Typography variant="caption" sx={{ color: '#f44336' }}>
                    Errors ({validationResults.errors.length})
                  </Typography>
                </Box>
              )}
              {validationResults.warnings?.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                    borderLeft: '3px solid #ff9800',
                    borderRadius: '2px'
                  }} />
                  <Typography variant="caption" sx={{ color: '#ff9800' }}>
                    Warnings ({validationResults.warnings.length})
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            Raw EDI Content
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Format toggle buttons */}
            <ToggleButtonGroup
              size="small"
              value={formatType}
              exclusive
              onChange={handleFormatChange}
              aria-label="formatting options"
              sx={{ mr: 1 }}
            >
              <ToggleButton value="raw" aria-label="raw format">
                <Tooltip title="Raw content">
                  <DataObjectIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="segment" aria-label="segment format">
                <Tooltip title="Format by segment">
                  <FormatAlignLeftIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="element" aria-label="element format">
                <Tooltip title="Format by element">
                  <FormatAlignCenterIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            
            {/* Font size controls */}
            <Box sx={{ display: 'flex', mr: 1 }}>
              <Tooltip title="Decrease font size">
                <IconButton 
                  size="small" 
                  onClick={decreaseFontSize}
                  aria-label="Decrease font size"
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Increase font size">
                <IconButton 
                  size="small" 
                  onClick={increaseFontSize}
                  aria-label="Increase font size"
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Copy button */}
            <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
              <ToggleButton
                value="copy"
                selected={copySuccess}
                onChange={copyToClipboard}
                aria-label="copy to clipboard"
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </Box>
        </Toolbar>
        
        <Box
          sx={{
            p: 2,
            backgroundColor: '#282a36',
            color: '#f8f8f2',
            fontFamily: '"Roboto Mono", monospace',
            fontSize: `${fontSize}px`,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowX: 'auto',
            lineHeight: 1.5,
            maxHeight: '600px',
            overflowY: 'auto'
          }}
        >
          {formatContent()}
        </Box>
      </Paper>
    </Box>
  );
};

export default RawEdiViewer; 